import environments from './XEnvironment.js';
import { XTemplateDOM } from './XDOM.js';
import { xArray, xObject, xReference, xString } from './XTypes.js';

import template from './XSection.html?raw';

import XScrollTranslator from './XScrollTranslator.js';

import Plyr from 'plyr';

export class XMedia
{
    #section;
    #element;
    #blockReferences = [];

    constructor(section)
    {
        this.#section = section;

        this.#element = document.createElement('div');
        this.#element.classList.add('x-aspect-ratio-content');
    }

    addBlockReference(block)
    {
        this.#blockReferences.push(block);
    }

    hide()
    {
        this.#element.style.display = 'none';
    }
    
    show()
    {
        this.#section.hideAllMedia();
        this.#element.style.removeProperty('display');
    }

    destroy()
    {

    }

    get section         () { return this.#section;         }
    get element         () { return this.#element;         }
    get blockReferences () { return this.#blockReferences; }
}

function xDetectBestResolutionForElement(element, resolutions)
{
    if(resolutions.length > 0)
    {
        const boundingClientRect = element.getBoundingClientRect();
        if(boundingClientRect.width == 0 || boundingClientRect.height == 0)
        {
            return null;
        }
        const width = boundingClientRect.width * window.devicePixelRatio;
        const height = boundingClientRect.height * window.devicePixelRatio;
        let i;
        for(i = 0; i < resolutions.length-1; i++)
        {
            if(width <= resolutions[i].width || height <= resolutions[i].height)
            {
                break;
            }
        }
        return resolutions[i];
    }
    return null;
}

export class XMediaImage extends XMedia
{
    #elementImage;
    #urlAsset;
    #extension;
    #resolutions;
    #fontLineHeight;
    #color;

    constructor(section, data, resizeContainer = environments.active.resizeContainer)
    {
        super(section);
        this.#urlAsset       = environments.active.urlAssets + data.$key + '/' + data.$version + '/' + data.name + '/';
        this.#extension      = data.extensions[0];
        this.#resolutions    = data.resolutions;
        this.#fontLineHeight = data.fontLineHeight;
        this.#color          = data.color;

        this.#resolutions.sort((a,b) => a.width - b.width);
        
        this.#elementImage = document.createElement('div');
        this.#elementImage.classList.add('x-image');
        this.element.classList.add('x-flex-center');
        this.element.append(this.#elementImage);

        resizeContainer.add(this);
    };

    _handleResize()
    {
        if(!this.#urlAsset)
        {
            return;
        }
        const bestResolution = xDetectBestResolutionForElement(this.element.parentElement, this.#resolutions);
        if(bestResolution)
        {
            const src = this.#urlAsset + String(bestResolution.width) + 'x' + String(bestResolution.height) + '.' + this.#extension;
            this.#elementImage.style.backgroundImage = 'url(' + src + ')';
            if(this.#fontLineHeight)
            {
                const relatedTextHeight = 20;
                this.#elementImage.style.maxHeight = String(bestResolution.height / this.#fontLineHeight * relatedTextHeight) + 'px';
            }
        }
    }

    destroy()
    {
        this._resizeContainer?.remove(this);
    }
}

export class XMediaTitle extends XMediaImage
{
    constructor(section, title, data, resizeContainer = environments.active.resizeContainer)
    {
        super(section, data, resizeContainer);

        const elementTitle = document.createElement('div');
        elementTitle.classList.add('x-media-title');
        elementTitle.innerText = title;
        this.element.append(elementTitle);
    };
}

export class XMediaVideo extends XMedia
{
    #urlAsset;
    #extension;
    #resolutions;
    #hasAudio;
    #color;

    #blockLastReferenced;
    #seeking = false;
    #plyr;
    #initialState = null;

    constructor(section, data, resizeContainer = environments.active.resizeContainer)
    {
        super(section);
        this.#urlAsset    = environments.active.urlAssets + data.$key + '/' + data.$version + '/' + data.name + '/';
        this.#extension   = data.extensions[0];
        this.#resolutions = data.resolutions;
        this.#hasAudio    = data.hasAudio;
        this.#color       = data.color;

        this.#resolutions.sort((a,b) => a.width - b.width);
        
        const elementVideo = document.createElement('video');
        elementVideo.setAttribute('preload', 'none');
        elementVideo.setAttribute('playsinline', '');
        this.element.append(elementVideo);

        resizeContainer.add(this);
    };

    _handleResize()
    {
        if(!this.#urlAsset)
        {
            return;
        }
        const bestResolution = xDetectBestResolutionForElement(this.element.parentElement, this.#resolutions);
        if(bestResolution)
        {
            if(!this.#plyr)
            {
                this.#initialState = {
                    currentTime: 0,
                    volume: this.section.content.masterVolume
                };
                const qualityLabel = {};
                const qualityBadge = {};
                for(const resolution of this.#resolutions)
                {
                    const min = Math.min(resolution.width, resolution.height);
                    const label = String(resolution.width) + 'x' + String(resolution.height);
                    qualityLabel[resolution.height] = label;
                    if(min >= 2160)
                    {
                        qualityBadge[resolution.height] = '4K';
                    }
                    else if(min >= 720)
                    {
                        qualityBadge[resolution.height] = 'HD';
                    }
                    else if(min >= 480)
                    {
                        qualityBadge[resolution.height] = 'SD';
                    }
                }
                this.#plyr = new Plyr(this.element.firstElementChild, {
                    settings: [
                        'quality'
                    ],
                    invertTime: false,
                    controls: [
                        'play-large', // The large play button in the center
                        //'restart', // Restart playback
                        //'rewind', // Rewind by the seek time (default 10 seconds)
                        'play', // Play/pause playback
                        //'fast-forward', // Fast forward by the seek time (default 10 seconds)
                        'progress', // The progress bar and scrubber for playback and buffering
                        'current-time', // The current time of playback
                        //'duration', // The full duration of the media
                        //'mute', // Toggle mute
                        //'volume', // Volume control
                        'captions', // Toggle captions
                        'settings', // Settings menu
                        //'pip', // Picture-in-picture (currently Safari only)
                        //'airplay', // Airplay (currently Safari only)
                        //'download', // Show a download button with a link to either the current source or a custom URL you specify in your options
                        //'fullscreen', // Toggle fullscreen
                    ],
                    storage: {
                        enabled: false
                    },
                    quality: { 
                        default: bestResolution.height, 
                        options: this.#resolutions.map(resolution => resolution.height)
                    },
                    i18n: {
                        qualityLabel: qualityLabel,
                        qualityBadge: qualityBadge
                    },
                    markers: {
                        enabled: true,
                        points: (()=>
                        {
                            let points = [];
                            for(const referenceBlock of this.blockReferences)
                            {
                                if('timestamp' in referenceBlock.mediaData)
                                {
                                    points.push({
                                        time: referenceBlock.mediaData.timestamp,
                                        label: ''
                                    });
                                }
                            }
                            return points;
                        })()
                    },
                    ratio: '16:9'
                });
                this.#plyr.once('canplay', () => 
                {
                    this.#plyr.currentTime = this.#initialState.currentTime;
                    this.#plyr.volume = this.#initialState.volume;
                    this.#initialState = null;
                    const elementSeekSlider = this.element.querySelector('input[data-plyr="seek"]');
                    const startSeek = () => { this.#seeking = true; };
                    const stopSeek = () => { this.#seeking = false; };
                    elementSeekSlider.addEventListener('mousedown', startSeek);
                    elementSeekSlider.addEventListener('touchstart', startSeek);
                    elementSeekSlider.addEventListener('mouseup', stopSeek);
                    elementSeekSlider.addEventListener('touchend', stopSeek);
                });
                this.#plyr.on('timeupdate', () => 
                {
                    if(this.#plyr.playing || this.#seeking)
                    {
                        const block = this.#getBlockReferenceFromTime(this.#plyr.currentTime);
                        if(this.#plyr.playing)
                        {
                            if(block != this.#blockLastReferenced)
                            {
                                block.play({
                                    doNotSetCurrentTime: true
                                });
                                this.#blockLastReferenced = block;
                            }
                        }
                        else
                        {
                            block.scrollHere();
                        }
                    }
                });
                this.#plyr.on('playing', () => 
                {
                    this.#blockLastReferenced = this.#getBlockReferenceFromTime(this.#plyr.currentTime + 0.1);
                    this.#blockLastReferenced.play({
                        doNotSetCurrentTime: true
                    });
                });
                this.#plyr.on('pause', () => 
                {
                    if(!this.#blockLastReferenced)
                    {
                        return;
                    }
                    if(this.#plyr.ended)
                    {
                        this.#blockLastReferenced.onMediaEnded();
                    }
                    else
                    {
                        this.#blockLastReferenced.stop();
                    }
                    this.#blockLastReferenced = null;
                });

                this.#plyr.source = {
                    type: 'video',
                    sources: this.#resolutions.map((resolution) => {
                        return {
                            src: this.#urlAsset + String(resolution.width) + 'x' + String(resolution.height) + '.' + this.#extension,
                            type: 'video/' + this.#extension,
                            size: resolution.height
                        };
                    }),
                    //poster: '/path/to/poster.jpg',
                    /*
                    previewThumbnails: {
                        enabled: true,
                        src: this.#urlAsset + 'storyboard.vtt'
                    }
                    */
                };
            }
            this.#plyr.quality = bestResolution.height;
        }
    }

    #getBlockReferenceFromTime(time)
    {
        for(let i = this.blockReferences.length-1; i > 0; i--)
        {
            const block = this.blockReferences[i];
            const timestamp = block.mediaData?.timestamp ?? false;
            if(timestamp !== false && time >= timestamp)
            {
                return block;
            }
        }
        return this.blockReferences[0];
    };

    setCurrentTime(currentTime)
    {
        const target = this.#initialState != null ? this.#initialState : this.#plyr;
        target.currentTime = currentTime;
        this.#blockLastReferenced = this.#getBlockReferenceFromTime(currentTime + 0.1);
    };

    get isPlaying()
    {
        return this.#plyr.playing;
    };

    set volume(volume)
    {
        const target = this.#initialState != null ? this.#initialState : this.#plyr;
        target.volume = volume;
    }

    play()
    {
        this.#blockLastReferenced = this.#getBlockReferenceFromTime(this.#plyr.currentTime + 0.1);
        this.#plyr.play().catch(() => 
        {
            if(this.#blockLastReferenced)
            {
                this.#blockLastReferenced.stop();
            }
        });
    };

    pause()
    {
        this.#blockLastReferenced = null;
        this.#plyr.pause();
    };

    get hasAudio()
    {
        return this.#hasAudio;
    };

    show(blockSource)
    {
        super.show();
        if(!blockSource)
        {
            return;
        }
        const timestamp = blockSource.mediaData?.timestamp ?? false;
        if(timestamp !== false && !this.#plyr.playing && !this.#seeking)
        {
            this.setCurrentTime(timestamp);
        }
    }

    destroy()
    {
        this.#plyr.destroy();
        this.#plyr = null;
        this._resizeContainer?.remove(this);
    }
}

export class XSection
{
    static get type () { return 'XSection'; }

    #dom;
    #content;
    #page;
    #site;

    #blocks;
    #blocksFlattened;
    #blockFocus = null;

    #media = {};

    #element;
    #elementText;
    #elementSpacer;

    #mediaInitialized = false;
    #mediaCanvasEnabled;
    #elementTextOccluder;
    #elementMediaCanvas;
    #elementMediaContainer;

    #textHeight = 0;

    #scrollTranslator;

    constructor(dom, content, page, site)
    {
        this.#dom     = dom;
        this.#content = content;
        this.#page    = page;
        this.#site    = site;

        this.#element = this.#dom._element;
        this.#elementText = this.#dom.text._element;
        this.#elementSpacer = this.#dom.text.spacer._element;

        this.#elementTextOccluder = this.#dom.textOccluder._element;
        this.#elementMediaCanvas = this.#dom.mediaCanvas._element;
        this.#elementMediaContainer = this.#dom.mediaCanvas.mediaContainer._element;
        this.#mediaCanvasEnabled = true;
        
        const domTypes = environments.active.domTypes;
        this.#blocks = this.#dom.text.blocks._setCreator(() => ({
                     XBlock: model => new domTypes.XBlock         (model, this, this, content, page, site),
            XBlockStructure: model => new domTypes.XBlockStructure(model, this, this, content, page, site),
        }));
        this.updateBlocksFlattened();

        /*
        requestAnimationFrame(() => {
            setTimeout(() => {
                this.updateMedia();
            }, 0);
        });
        */
        
        this.#scrollTranslator = new XScrollTranslator((scrollTop, blockID, position, t, status) =>
        {
            this.#elementText.scrollTop = scrollTop;
            if(status == 0)
            {
                this.#blocksFlattened[blockID].setHighlightFocus(true);
            }
            else
            {
                this.unhighlightFocusBlocks();
            }
        });
    };

    hideAllMedia()
    {
        for(const mediaID in this.#media)
        {
            const media = this.#media[mediaID];
            media.hide();
        }
    };

    unhighlightMediaBlocks()
    {
        for(const block of this.#blocksFlattened)
        {
            block.setHighlightMedia(false);
        }
    };

    unhighlightFocusBlocks()
    {
        for(const block of this.#blocksFlattened)
        {
            block.setHighlightFocus(false);
        }
    };

    updateBlocksFlattened()
    {
        this.#blocksFlattened = this.calculateBlocksFlattened();
        this.#page.scheduleLayout();
    }

    unloadMedia()
    {
        for(const mediaID in this.#media)
        {
            const media = this.#media[mediaID];
            media.destroy();
        }
        this.#elementMediaContainer.innerHTML = '';
        this.#media = {};
        this.#mediaInitialized = false;
    }

    updateMedia(initialize=false)
    {
        if(initialize && this.#mediaInitialized)
        {
            return;
        }
        this.unloadMedia();
        let firstMedia = null;
        let currentBlockMedia = null;
        for(const block of this.#blocksFlattened)
        {
            block.updateButtons();
            if(block.mediaID)
            {
                block.media = currentBlockMedia?.media;
                currentBlockMedia = block;
            }
            block.blockMedia = currentBlockMedia;
            if(!block.mediaID)
            {
                continue;
            }
            let media = null;
            const mediaID = block.mediaID;
            if(mediaID in this.#media)
            {
                this.#media[mediaID].addBlockReference(block);
                continue;
            }
            if(block.mediaType == 'title')
            {
                const mediaData = block.mediaData;
                const data = this.#page.getDataForKey(mediaData.imageID);
                if(!data)
                {
                    continue;
                }
                if(data.$ == 'XImage')
                {
                    media = new XMediaTitle(this, mediaData.text, data, this.#page.sectionResizeContainer);
                }
            }
            else
            {
                const data = this.#page.getDataForKey(mediaID);
                if(!data)
                {
                    continue;
                }
                if(data.$ == 'XImage')
                {
                    media = new XMediaImage(this, data, this.#page.sectionResizeContainer);
                }
                else if(data.$ == 'XVideo')
                {
                    media = new XMediaVideo(this, data, this.#page.sectionResizeContainer);
                }
            }
            this.#media[mediaID] = media;
            this.#elementMediaContainer.append(media.element);
            media.addBlockReference(block);
            block.media = media;
            if(!firstMedia)
            {
                firstMedia = media;
            }
        }
        for(const mediaID in this.#media)
        {
            const media = this.#media[mediaID];
            media._handleResize();
        }
        this.hideAllMedia();
        if(this.#blockFocus)
        {
            this.#blockFocus.blockMedia?.setHighlightMedia(true);
        }
        else
        {
            firstMedia?.show();
        }
        this.#mediaInitialized = true;
    }

    onResize(currentSourceScroll)
    {
        const boundingClientRectText = this.#elementText.getBoundingClientRect();
        this.#textHeight = this.#page.mainVisibleHeight;
        let mediaHeight = 0;
        if(this.#mediaCanvasEnabled)
        {
            mediaHeight = boundingClientRectText.top - this.#elementMediaCanvas.getBoundingClientRect().top;
            this.#textHeight -= mediaHeight;
        }
        if(this.#elementText)
        {
            this.#elementText.scrollTop = 0;
            this.#elementText.style.height = String(this.#textHeight) + 'px';
        }
        /*
        for(const media of this.media)
        {
            media.onResize();
        }
        */

        let blocksHeight = 0;
        let boundingClientRectBlocks = [];
        for(const block of this.#blocksFlattened)
        {
            const boundingClientRectBlock = block.element.getBoundingClientRect();
            blocksHeight += boundingClientRectBlock.height;
            boundingClientRectBlocks.push(boundingClientRectBlock);
        }

        this.#elementSpacer.style.removeProperty('height');
        //let spacerHeight = Math.max(Math.min(this.#textHeight, blocksHeight), this.#elementText.scrollHeight) - this.#textHeight;
        let spacerHeight = Math.max(0.5 * this.#textHeight, this.#elementText.scrollHeight < this.#textHeight ? this.#textHeight : (this.#elementText.scrollHeight < (2.0 * this.#textHeight) ? (2.0 * this.#textHeight) - this.#elementText.scrollHeight : 0));
        blocksHeight += spacerHeight;
        this.#elementSpacer.style.height = String(spacerHeight) + 'px';

        let scrollHeight = this.#elementText.scrollHeight - this.#textHeight;
        //let scrollHeight = this.#elementText.scrollHeight;
        
        let pauseHeight = 500;
        this.#scrollTranslator.reset();
        this.#scrollTranslator.addBreakpoint(currentSourceScroll, 0, 0);
        //currentSourceScroll += pauseHeight;
        //this.#scrollTranslator.addBreakpoint(currentSourceScroll, 0);

        let absoluteScrollHeight = 0;
        for(let i = 0; i < this.#blocksFlattened.length; i++)
        {
            const block = this.#blocksFlattened[i];
            const blockHeight = boundingClientRectBlocks[i].height;
            //const relativeScrollHeight = (blockHeight / blocksHeight) * scrollHeight;
            const relativeScrollHeight = ((blockHeight + spacerHeight * (blockHeight / blocksHeight)) / blocksHeight) * scrollHeight;
            block.onResize(currentSourceScroll, boundingClientRectBlocks[i].top - boundingClientRectText.top);
            absoluteScrollHeight += relativeScrollHeight;
            currentSourceScroll += relativeScrollHeight;
            this.#scrollTranslator.addBreakpoint(currentSourceScroll, absoluteScrollHeight, Math.min(i+1, this.#blocksFlattened.length-1));
        }
        //const relativeScrollHeight = (spacerHeight / blocksHeight) * scrollHeight;
        //absoluteScrollHeight += relativeScrollHeight;
        //currentSourceScroll += relativeScrollHeight;
        //this.#scrollTranslator.addBreakpoint(currentSourceScroll, absoluteScrollHeight, this.#blocksFlattened.length-1);
        //currentSourceScroll -= 0.5 * this.#textHeight;

        //currentSourceScroll += pauseHeight;
        //this.#scrollTranslator.addBreakpoint(currentSourceScroll, scrollHeight);
        return currentSourceScroll;
    };

    onScroll(position)
    {
        this.updateMedia(true);
        this.#scrollTranslator.onScroll(position);

        for(const block of this.blocksFlattened)
        {
            if(block.elementAnchor)
            {
                block.elementAnchor.classList.toggle('x-highlight', block.isVisible);
            }
        }
    }

    calculateBlocksFlattened()
    {
        const blocks = [];
        for(const block of this.#blocks)
        {
            blocks.push(...block.calculateBlocksFlattened());
        }
        return blocks;
    }

    get blockDepth()
    {
        return 0;
    }

    get previous()
    {
        const sections = this.#content.sections;
        const index = sections.indexOf(this);
        return index > -1 ? sections[index - 1] : undefined;
    }

    get next()
    {
        const sections = this.#content.sections;
        const index = sections.indexOf(this);
        return index > -1 ? sections[index + 1] : undefined;
    }

    set blockFocus(blockFocus) { this.#blockFocus = blockFocus; }

    get _dom            () { return this.#dom;                }
    get parent          () { return this.#content;            }
    get content         () { return this.#content;            }
    get page            () { return this.#page;               }
    get site            () { return this.#site;               }
    get blocks          () { return this.#blocks;             }
    get blocksFlattened () { return this.#blocksFlattened;    }
    get blockFocus      () { return this.#blockFocus;         }
    get media           () { return this.#media;              }
    get element         () { return this.#element;            }
    get elementText     () { return this.#elementText;        }
    get elementSpacer   () { return this.#elementSpacer;      }
};

export class XSectionReactive extends XSection
{
    static get specification () 
    {
        return xObject({
            $: XSection.type,
            blocks: xArray(xReference(['XBlock', 'XBlockStructure']))
        });
    }

    static #template;

    static initDOM(domEnvironment)
    {
        this.#template = new XTemplateDOM(template, XSectionReactive.specification, domEnvironment);
    }

    #data;

    constructor(data, content, page, site)
    {
        super(XSectionReactive.#template._createAccessibleDOM(data), content, page, site);
        this.#data = data;

        this._dom.text.blocks._addAttachment({
            _create: () => {
                this.updateBlocksFlattened();
            }
        }, false);

        if(this.site.isEditor)
        {
            this._dom.text.editor?.addAfter._element.addEventListener('click', (event) => 
            {
                this.addNewAfter();
                event.stopPropagation();
            });
        }
        if(this.site.isTranslator)
        {
            this._dom.text.editor._destroy();
            delete this._dom.text.editor;
        }
    };

    index()
    {
        return this.parent.sections?.indexOf(this);
    }

    remove()
    {
        const index = this.parent.sections.indexOf(this);
        if(index > -1)
        {
            this.parent.data.sections.splice(index, 1);
            return true;
        }
        return false;
    }

    addNewAfter(type)
    {
        const index = this.index();
        const section = this.createNewDataWithBlock(type);
        this.parent.data.sections.splice(index+1, 0, section._key);
    }

    createNewDataWithBlock()
    {
        const section = this.site.storage.addData('XSection');
        const block = this.site.storage.addData('XBlock');
        const textVoice = this.site.storage.addData('XTextVoice');
        block.type = 'h1';
        block.textVoice = textVoice._key;
        textVoice.text = 'NEW SECTION';
        section.blocks.push(block._key);
        return section;
    }

    collectStaticStorageIDs(collectedIDs)
    {
        for(const block of this.blocksFlattened)
        {
            block.collectStaticStorageIDs(collectedIDs);
        }
    }

    get data () { return this.#data; }
};
